<script lang="ts" setup>
import { computed } from 'vue';

import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';

import { AppBanner, AppRouterLink, AppSvgIcon } from '@libs/ui';
import type { IngredientId } from '@libs/trip-planner-interfaces';

import type {
  ItinerarySegmentType,
  UpsellProductCardData,
} from '../../../models';
import { useRouterStore } from '../../../../../core/stores/routerStore';
import ItineraryTimeline from '../ItineraryTimeline.vue';
import ItineraryIcon from '../ItineraryIcon.vue';
import { useTripOverviewPanelsStore } from '../../../stores';

import SuggestedActivitiesBanner from './SuggestedActivitiesBanner.vue';
import StaticSuggestedActivitiesBanner from './StaticSuggestedActivitiesBanner.vue';

interface ItineraryUpsellSegmentProps {
  data: UpsellProductCardData;
  segmentType: ItinerarySegmentType;
  isLoading?: boolean;
}

const props = defineProps<ItineraryUpsellSegmentProps>();

const routerStore = useRouterStore();
const recipeStore = useRecipeStore();
const statsigEventsStore = useStatsigEventsStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();

const ingredientIndex = computed(() =>
  recipeStore.getIngredientIndex(props.data.ingredientId)
);

function showActivities(): void {
  routerStore.navigateToActivitySelection(ingredientIndex.value);
  statsigEventsStore.logTripOverviewExploreActivitiesClicked();
}

function openActivity(activityCode: string, ingredientId: IngredientId): void {
  tripOverviewPanelsStore.openActivityDetailsPanel(activityCode, ingredientId);
}
</script>

<template>
  <ItineraryTimeline
    v-if="
      props.data.genericActivityBanner.isVisible ||
      props.data.suggestedActivitiesBanner.isVisible
    "
    :segment-type="props.segmentType"
  >
    <template #icon>
      <ItineraryIcon
        v-if="props.data.suggestedActivitiesBanner.isVisible"
        :segment-type="props.segmentType"
      />
    </template>
    <template v-if="props.data.suggestedActivitiesBanner.isVisible" #header>
      <div class="segment-header">
        {{
          $t('planner.activity_suggestions.things_to_do', [
            props.data.suggestedActivitiesBanner.destination,
          ])
        }}
      </div>
    </template>

    <template #content>
      <slot />
      <SuggestedActivitiesBanner
        v-if="props.data.suggestedActivitiesBanner.isVisible"
        :cards="props.data.suggestedActivitiesBanner.suggestedActivities"
        :destination="props.data.suggestedActivitiesBanner.destination"
        :ingredient-index="ingredientIndex"
        @open-activity="openActivity($event, props.data.ingredientId)"
      />

      <AppBanner
        v-if="props.data.genericActivityBanner.isVisible && !props.isLoading"
        :title="$t('activities_banner.title')"
        :subtitle="$t('activities_banner.content')"
        type="secondary"
        color="purple"
        class="activities-banner"
        @click="showActivities"
      >
        <template #right-icon>
          <div class="see-activities-link-wrapper">
            <AppRouterLink
              :link="routerStore.getActivitySelectionUrl(ingredientIndex)"
              class="see-activities-link"
              font="font-body-s-bold"
              color="color-neutral-70"
            >
              <span class="see-activities-text">
                {{ $t('activities_banner.button') }}
              </span>
            </AppRouterLink>
            <AppSvgIcon name="arrowRight" color="color-neutral-70" />
          </div>
        </template>
      </AppBanner>
    </template>
  </ItineraryTimeline>

  <StaticSuggestedActivitiesBanner
    v-if="props.data.staticSuggestedActivitiesBanner.isVisible"
    :cards="props.data.staticSuggestedActivitiesBanner.suggestedActivities"
    :destination="props.data.staticSuggestedActivitiesBanner.destination"
    :ingredient-index="ingredientIndex"
    @show-activities="showActivities"
    @open-activity="openActivity($event, props.data.ingredientId)"
  />
</template>

<style scoped lang="scss">
.segment-header {
  @include font-label-m-bold;
}

.see-activities {
  white-space: nowrap;
  text-decoration: underline;
}

.see-activities-link-wrapper {
  display: flex;
  flex-direction: row;
  white-space: nowrap;
  gap: var(--spacing-8);
  .see-activities-text {
    text-decoration: underline;
  }
}
</style>
